import React, { FunctionComponent, FormEvent, useState, useEffect, ChangeEventHandler } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import styled, { css } from 'styled-components';
import { Helmet } from 'react-helmet';
import { Scalars } from '../../generated/contentful-generated-types';

import {
  sendMarketingInformation,
  sendMarketingInformationVariables,
  MarketingInformationInput,
  validateVatNumber,
  validateVatNumberVariables,
  validateSiretNumber,
  validateSiretNumberVariables,
  GetFullCompanyInformation_fullCompanyInformation
} from '../../common/graphql';

import { getUrlOf } from '../../utils/urls';
import { ButtonColor, Justify, CustomizableButton } from '../../common/components/buttons/CustomizableButton';
import { isOther, isReseller } from '../../utils/registrationSuccess';
import { SiretInputShared } from '../../shared/component/SiretInputShared';
import { getColorFromCode } from '../../common/constants';
import { useGetLogin } from '../../hooks/useLogin';
import { useTranslation } from 'react-i18next';

const chevronBas = require('../../../assets/icons/chevron-back.svg');
const giftBox = require('../../../assets/icons/giftbox.svg');
const checkMark = require('../../../assets/icons/checked.svg');

export const LAST_URL_ENDPOINT = '/index/get-last-product-page-url-before-registration';

export const SEND_MARKETING_INFORMATION_MUTATION = gql`
  mutation sendMarketingInformation($input: MarketingInformationInput!) {
    sendMarketingInformation(input: $input) {
      error
      status
    }
  }
`;

export const VALIDATE_VAT_NUMBER_MUTATION = gql`
  mutation validateVatNumber($vatNumber: String!) {
    validateVatNumber(vatNumber: $vatNumber)
  }
`;

export const VALIDATE_SIRET_NUMBER_MUTATION = gql`
  mutation validateSiretNumber($siretNumber: String!) {
    validateSiretNumber(siretNumber: $siretNumber)
  }
`;

export const getFormData = (form: HTMLFormElement): FormData => {
  return new FormData(form);
};

interface FieldError {
  fieldName: string;
  errorText: string;
}

export type Props = Readonly<{
  isVisible?: boolean | null;
  setRegistrationSuccessPageIsVisible?: (param: boolean) => void;
  origin?: string | null;
  options: {
    positionOptions?: Scalars['JSON'] | null;
    companySizeOptions?: Scalars['JSON'] | null;
    companyActivitiesOptions?: Scalars['JSON'] | null;
    companyKindOptions?: Scalars['JSON'] | null;
    learnAboutOptions?: Scalars['JSON'] | null;
  };
}>;

export const RegistrationSuccess: FunctionComponent<Props> = props => {
  const locale = process.env.GATSBY_LOCALE || '';
  const { t } = useTranslation('translations');
  const resellerOnly = isReseller(props.origin);
  const comesFromOtherTypeProfile = isOther(props.origin);

  const { options, isVisible, setRegistrationSuccessPageIsVisible } = props;
  const [sendMarketingInformation] = useMutation<sendMarketingInformation, sendMarketingInformationVariables>(SEND_MARKETING_INFORMATION_MUTATION);
  const [validateVatNumber] = useMutation<validateVatNumber, validateVatNumberVariables>(VALIDATE_VAT_NUMBER_MUTATION);
  const [validateSiretNumber] = useMutation<validateSiretNumber, validateSiretNumberVariables>(VALIDATE_SIRET_NUMBER_MUTATION);

  const [submitted, setSubmitted] = useState(false);
  const [lastUrl, setLastUrl] = useState<string>('/');
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const getLastUrl = async () => {
    const response = await fetch(`${getUrlOf('ams')}${LAST_URL_ENDPOINT}`, { credentials: 'include' });
    const url = await response.text();
    if (url.length) {
      setLastUrl(url);
    }
  };

  const redirect = (link: string) => {
    window.location.href = link;
  };

  useEffect(() => {
    getLastUrl();
  }, []);

  const companyName = useGetLogin().data?.login?.companyName;

  const createOption = (data: Scalars['JSON']) => {
    const keys = Object.keys(data);
    const value = data[keys[0]];
    return (
      <option key={value} value={value}>
        {data[keys[1]]}
      </option>
    );
  };

  const createSelect = (
    labelTranslationKey: string,
    id: string,
    required = true,
    options?: Scalars['JSON'] | null,
    onChange?: ChangeEventHandler<HTMLSelectElement>
  ): JSX.Element => (
    <InputContainer>
      <Label htmlFor={id}>
        {t(labelTranslationKey)}
        {required && <sup className="required">*</sup>}
        {!required && <span className="optional">({t('gatsby.registrationSuccess.optional')})</span>}
      </Label>
      <SelectContainer>
        <Select id={id} name={id} defaultValue="" required={required} onChange={onChange}>
          <option value="" disabled>
            &nbsp;
          </option>
          {options &&
            options.map((data: Scalars['JSON']) => {
              return createOption(data);
            })}
        </Select>
      </SelectContainer>
    </InputContainer>
  );

  const createInput = ({
    labelTranslationKey,
    id,
    type,
    isRequired = false,
    placeholder = '',
    matchPattern,
    show,
    onClick,
    disabled,
    value,
    onChange,
    readonly
  }: {
    labelTranslationKey: string;
    id: string;
    type: string;
    isRequired?: boolean;
    placeholder?: string;
    matchPattern?: string;
    show?: boolean;
    onClick?: () => void;
    disabled?: boolean;
    value?: string;
    onChange?: (value: string) => void;
    readonly?: string;
  }): JSX.Element => {
    const error = fieldErrors.find(x => x.fieldName === id);
    return (
      <InputContainer show={show} type={type}>
        {type === 'text' && <Label htmlFor={id}>{t(labelTranslationKey)}</Label>}
        {!isRequired && type !== 'checkbox' && <span className="optional">({t('gatsby.registrationSuccess.optional')})</span>}
        <TextInput
          error={!!error}
          placeholder={placeholder}
          id={id}
          name={id}
          type={type}
          required={isRequired}
          pattern={matchPattern}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldErrors([]);
            if (onChange) {
              onChange(e.target.value);
            }
          }}
          onClick={onClick}
          disabled={disabled}
          value={value}
          readonly={readonly}
        ></TextInput>
        {type === 'checkbox' && <Label htmlFor={id}>{t(labelTranslationKey)}</Label>}
        {error && <ErrorParagraph>{error.errorText}</ErrorParagraph>}
      </InputContainer>
    );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);

    const errors: FieldError[] = [];
    const htmlFormElement = event.target as HTMLFormElement;

    try {
      // Get form structured data
      const formData = getFormData(htmlFormElement);

      // Validate the VAT number by checking if it's an already used number
      const vatNumber = formData.get('tva') as string;

      // Validate the Siret number by checking if it's an already used number
      const siretNumber = formData.get('siret') as string;

      if (vatNumber) {
        try {
          const result = await validateVatNumber({ variables: { vatNumber } });
          if (result.data && !result.data.validateVatNumber) {
            errors.push({
              fieldName: 'tva',
              errorText: t('gatsby.registrationSuccess.vatExistingError')
            });
          }
        } catch (e) {
          console.error(e);
          errors.push({
            fieldName: 'tva',
            errorText: t('gatsby.registrationSuccess.vatTechnicalError')
          });
        }
      }

      if (siretNumber) {
        try {
          const result = await validateSiretNumber({ variables: { siretNumber } });
          if (result.data && !result.data.validateSiretNumber) {
            setSiretError(t('gatsby.registrationSuccess.siretExistingError'));
            errors.push({ fieldName: 'siret', errorText: t('gatsby.registrationSuccess.siretExistingError') });
          } else {
            setSiretError('');
          }
        } catch (e) {
          console.error(e);
          setSiretError(t('gatsby.registrationSuccess.siretTechnicalError'));
          errors.push({ fieldName: 'siret', errorText: t('gatsby.registrationSuccess.siretTechnicalError') });
        }
      }

      // Do not go further in the submission process if the provided VAT number is wrong
      if (errors.length) {
        setFieldErrors([...fieldErrors, ...errors]);
        return;
      }

      // Initialize the mutation input with default values
      const input: MarketingInformationInput = {
        companyLegalForm: '',
        companyActivity: '',
        companySize: '',
        userFunction: '',
        tva: '',
        siret: '',
        companyName: '',
        address: '',
        companyPrincipalActivity: '',
        nj: '',
        njLabel: '',
        principalActivityLabel: '',
        staffCount: '',
        staffCountLabel: '',
        creationDate: '',
        caSlice: '',
        dissolutionDate: '',
        updateOrigin: '',
        updateDate: '',
        exploitationName: '',
        companyDimension: '',
        nafCode: '',
        city: '',
        country: '',
        region: '',
        seasonalActivity: '',
        acronym: '',
        headquarters: '',
        address2: '',
        siren: '',
        postalCode: ''
      };

      // Fill input values from structured form data
      Object.entries(input).forEach(([key]) => ((input as any)[key] = formData.get(key) || ''));

      // Call mutation but do not care about mutation result because we cannot do much here if something went wrong
      try {
        // Since we cannot do much if something went wrong I added a try catch here (to avoid blocking the whole
        // process if something goes wrong)
        await sendMarketingInformation({ variables: { input } });
      } catch (error) {
        console.log(`error from marking info: ${error}`);
      }
      setSubmitted(true);
      setFieldErrors([]);
    } finally {
      setSubmitting(false);
    }
  };

  const Submitted = () => (
    <SubmittedIndicatorContainer>
      <SubmittedIndicator />
      <SuccessText>{t('gatsby.registrationSuccess.successThanks')}</SuccessText>
      <SuccessDetailsText>{t('gatsby.registrationSuccess.successThanksDetails')}</SuccessDetailsText>
      <ContinueShoppingLink onClick={() => (isVisible && setRegistrationSuccessPageIsVisible ? setRegistrationSuccessPageIsVisible(false) : redirect(lastUrl))}>
        {t('gatsby.registrationSuccess.continueShopping')}
      </ContinueShoppingLink>
    </SubmittedIndicatorContainer>
  );
  const [companyInfo, setCompanyInfo] = useState<GetFullCompanyInformation_fullCompanyInformation | null>();
  const [siretError, setSiretError] = useState('');
  const updateCompanyInfo = (data: GetFullCompanyInformation_fullCompanyInformation | null) => {
    setCompanyInfo(data);
    setSiretError('');
  };

  const ResellerTeasing = () => (
    <ResellerTeasingContainer>
      <ResellerTeasingContentContainer>
        <ResellerTeasingContent>
          <ResellerTeasingLeft></ResellerTeasingLeft>
          <ResellerTeasingRight>
            {t('gatsby.registrationSuccess.resellerTeasing1')} <strong>{t('gatsby.registrationSuccess.resellerTeasing2')}</strong>{' '}
            {t('gatsby.registrationSuccess.resellerTeasing3')} <strong>{t('gatsby.registrationSuccess.resellerTeasing4')}</strong>
          </ResellerTeasingRight>
        </ResellerTeasingContent>
      </ResellerTeasingContentContainer>
    </ResellerTeasingContainer>
  );

  return (
    <>
      <Helmet>
        <title>{t('gatsby.registrationSuccess.headerResellerUppercase')}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <MainContainer>
        <HeaderContainer>
          <Header>
            {resellerOnly && (
              <>
                <HeaderUppercase>{t('gatsby.registrationSuccess.headerResellerUppercase')}</HeaderUppercase>
              </>
            )}
            {!resellerOnly && <HeaderUppercase>{t('gatsby.registrationSuccess.headerNonReseller')}</HeaderUppercase>}
            {resellerOnly && locale === 'fr' && <ResellerTeasing />}
            {resellerOnly && <HeaderResellerBaseline>{t('gatsby.registrationSuccess.headerReseller')}</HeaderResellerBaseline>}
          </Header>
        </HeaderContainer>
        <HatContainer>
          {resellerOnly && (
            <SubtitleContainer>
              <b>{t('gatsby.registrationSuccess.hatResellerIntro')}</b>
            </SubtitleContainer>
          )}
          {!resellerOnly && (
            <SubtitleContainer>
              <b>{t('gatsby.registrationSuccess.hatNonResellerIntro')}</b>
              {locale === 'fr' && <span>{t('gatsby.registrationSuccess.headerSubtitleNonReseller')}</span>}
            </SubtitleContainer>
          )}
        </HatContainer>
        <Content>
          {submitted ? (
            <Submitted />
          ) : (
            <>
              <FormContainer onSubmit={submit}>
                <SiretInputShared
                  label={t('gatsby.registrationSuccess.siret')}
                  inputType="number"
                  parentErrorMessage={siretError}
                  updateFieldsCallback={(data: GetFullCompanyInformation_fullCompanyInformation | null) => updateCompanyInfo(data)}
                />
                {createSelect('gatsby.registrationSuccess.myPosition', 'userFunction', !comesFromOtherTypeProfile, options.positionOptions)}
                {createSelect('gatsby.registrationSuccess.myActivity', 'companyActivity', !comesFromOtherTypeProfile, options.companyActivitiesOptions)}
                {createSelect('gatsby.registrationSuccess.myCompanySize', 'companySize', !comesFromOtherTypeProfile, options.companySizeOptions)}
                {createSelect('gatsby.registrationSuccess.companyKind', 'companyLegalForm', !comesFromOtherTypeProfile, options.companyKindOptions)}
                {createInput({
                  id: 'siret',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.siret',
                  value: companyInfo?.siret || '',
                  show: false
                })}
                {createInput({
                  id: 'tva',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.vatNumber',
                  value: companyInfo?.tvaIntra ? companyInfo.tvaIntra.split(' ').join('') : '',
                  show: false
                })}
                {createInput({
                  id: 'companyName',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.companyName',
                  value: companyInfo?.companyName || companyName || '',
                  show: true,
                  readonly: 'readonly'
                })}
                {createInput({
                  id: 'address',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.address',
                  value: [companyInfo?.streetNumber, companyInfo?.repetitionIndex, companyInfo?.streetType, companyInfo?.streetLabel].filter(String).join(' '),
                  show: false
                })}
                {createInput({
                  id: 'companyPrincipalActivity',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.companyPrincipalActivity',
                  value: companyInfo?.companyPrincipalActivity || '',
                  show: false
                })}
                {createInput({
                  id: 'nj',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.nj',
                  value: companyInfo?.nj || '',
                  show: false
                })}
                {createInput({
                  id: 'njLabel',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.njLabel',
                  value: companyInfo?.njLabel || '',
                  show: false
                })}
                {createInput({
                  id: 'principalActivityLabel',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.principalActivityLabel',
                  value: companyInfo?.principalActivityLabel || '',
                  show: false
                })}
                {createInput({
                  id: 'staffCount',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.staffCount',
                  value: companyInfo?.staffCount || '',
                  show: false
                })}
                {createInput({
                  id: 'staffCountLabel',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.staffCountLabel',
                  value: companyInfo?.staffCountLabel || '',
                  show: false
                })}
                {createInput({
                  id: 'creationDate',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.creationDate',
                  value: companyInfo?.creationDate || '',
                  show: false
                })}
                {createInput({
                  id: 'caSlice',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.caSlice',
                  value: companyInfo?.caSlice || '',
                  show: false
                })}
                {createInput({
                  id: 'dissolutionDate',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.dissolutionDate',
                  value: (companyInfo?.updateOrigin === 'E' && companyInfo?.updateDate) || '',
                  show: false
                })}
                {createInput({
                  id: 'updateOrigin',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.updateOrigin',
                  value: companyInfo?.updateOrigin || '',
                  show: false
                })}
                {createInput({
                  id: 'updateDate',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.updateDate',
                  value: companyInfo?.updateDate || '',
                  show: false
                })}
                {createInput({
                  id: 'exploitationName',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.exploitationName',
                  value: companyInfo?.exploitationName || '',
                  show: true,
                  disabled: false,
                  onChange: value => {
                    if (companyInfo) {
                      companyInfo.exploitationName = value;
                      setCompanyInfo(companyInfo);
                    }
                  }
                })}
                {createInput({
                  id: 'companyDimension',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.companyDimension',
                  value: companyInfo?.staffCountLabel || '',
                  show: false
                })}
                {createInput({
                  id: 'nafCode',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.nafCode',
                  value: companyInfo?.nafCode || '',
                  show: false
                })}
                {createInput({
                  id: 'address2',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.address2',
                  value: companyInfo?.address2 || '',
                  show: false
                })}
                {createInput({
                  id: 'postalCode',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.postalCode',
                  value: companyInfo?.postalCode || '',
                  show: false
                })}
                {createInput({
                  id: 'city',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.city',
                  value: companyInfo?.city || '',
                  show: false
                })}
                {createInput({
                  id: 'country',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.country',
                  value: companyInfo?.country || '',
                  show: false
                })}
                {createInput({
                  id: 'region',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.region',
                  value: companyInfo?.region || '',
                  show: false
                })}
                {createInput({
                  id: 'seasonalActivity',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.seasonalActivity',
                  value: companyInfo?.seasonalActivity || '',
                  show: false
                })}
                {createInput({
                  id: 'acronym',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.acronym',
                  value: companyInfo?.acronym || '',
                  show: false
                })}
                {createInput({
                  id: 'headquarters',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.headquarters',
                  value: companyInfo?.headquarters || '',
                  show: false
                })}
                {createInput({
                  id: 'updateIndex1',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.updateIndex1',
                  value: companyInfo?.updateIndex1 || '',
                  show: false
                })}
                {createInput({
                  id: 'updateIndex2',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.updateIndex2',
                  value: companyInfo?.updateIndex2 || '',
                  show: false
                })}
                {createInput({
                  id: 'updateIndex3',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.updateIndex3',
                  value: companyInfo?.updateIndex3 || '',
                  show: false
                })}
                {createInput({
                  id: 'siren',
                  type: 'text',
                  labelTranslationKey: 'gatsby.registrationSuccess.siren',
                  value: companyInfo?.siren || '',
                  show: false
                })}
                <CenteredButtonsContainer>
                  <CustomStyledButton
                    disabled={submitting}
                    justify={Justify.Center}
                    textColor={ButtonColor.White}
                    hoverTextColor={ButtonColor.Blue}
                    borderColor={ButtonColor.Blue}
                    backgroundColor={ButtonColor.Blue}
                    lowerCased
                  >
                    <ButtonPadder
                      dangerouslySetInnerHTML={{
                        __html: resellerOnly ? t('gatsby.registrationSuccess.submitReseller') : t('gatsby.registrationSuccess.submit')
                      }}
                    />
                  </CustomStyledButton>
                </CenteredButtonsContainer>
                <ContinueShoppingLink
                  onClick={() => (isVisible && setRegistrationSuccessPageIsVisible ? setRegistrationSuccessPageIsVisible(false) : redirect(lastUrl))}
                >
                  {t('gatsby.registrationSuccess.continueShopping')}
                </ContinueShoppingLink>
              </FormContainer>
            </>
          )}
        </Content>
        <Note>*{t('gatsby.registrationSuccess.mention')}</Note>
      </MainContainer>
    </>
  );
};

const SubtitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ResellerTeasingContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

const ResellerTeasingContent = styled.div`
  display: flex;
  align-items: center;
  background-color: #ffc74a;
  margin: 0 15px;
  padding: 20px;
`;

const ResellerTeasingContentContainer = styled.div`
  line-height: calc(10px + 0.7vw);
  font-size: calc(8px + 0.5vw);
  margin-top: 30px;
  @media (max-width: 425px) {
    width: 100%;
  }
`;

const ResellerTeasingLeft = styled.div`
  mask-image: url(${giftBox});
  mask-size: cover;
  margin-right: 20px;
  height: 50px;
  width: 50px;
  background-color: var(--header-text);
`;

const ResellerTeasingRight = styled.div``;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  margin-left: 0;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  width: 80%;
  align-items: center;
  align-self: center;
  font-size: 25px;
  font-weight: bold;

  @media (max-width: 768px) {
    line-height: calc(18px + 1.55vw);
    font-size: calc(18px + 0.55vw);
  }
`;

const Header = styled.div`
  color: var(--header-text);
  font-weight: normal;
  font-size: 20px;
  text-align: center;
  margin: 10px auto;
`;

const HeaderUppercase = styled.div`
  font-size: calc(16px + 0.5vw);
  text-align: center;
  font-weight: bold;
`;

const HatContainer = styled.div`
  line-height: calc(10px + 0.5vw);
  font-size: calc(8px + 0.5vw);
  padding: 10px 0 15px;
  line-height: 25px;
  text-align: center;
  width: 80%;
  align-self: center;
`;

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-wrap: wrap;
  height: 100%;

  & sup.required {
    color: red;
  }

  .optional {
    color: gray;
    font-size: 12px;
    margin-left: 4px;
  }
`;

interface InputContainerProps {
  show?: boolean;
  type?: string;
}

const InputContainer = styled.div<InputContainerProps>`
  ${props =>
    props.show !== undefined &&
    !props.show &&
    css`
      display: none;
    `}
  width: 80%;
  padding: 0 5% 2%;

  ${props =>
    props.type === 'checkbox' &&
    css`
      display: flex;
      flex-direction: row;

      & input {
        width: 15px;
        height: 15px;
        margin-top: 3px;
        margin-right: 5px;

        @media (max-width: 1280px) {
          width: 10%;
          margin-left: 2px;
        }

        @media (max-width: 768px) {
          width: 5%;
        }
      }

      & label {
        font-size: 14px;
      }
    `}
  @media(max-width: 768px) {
    width: 100%;
  }
`;

const CenteredContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const CenteredButtonsContainer = styled(CenteredContainer)`
  flex-direction: row;
`;

const ErrorParagraph = styled.p`
  padding: 10px 0;
  color: red;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px;
  margin: 0;
  box-shadow: 0 2px 4px 1px rgba(0, 0, 0, 0.1);
`;

const Label = styled.label`
  line-height: 20px;
  font-size: 14px;
`;

const SelectContainer = styled.div`
  position: relative;
  &::after {
    content: '';
    mask-image: url(${chevronBas});
    mask-repeat: no-repeat;
    transform: rotate(-90deg);
    background-color: var(--header-text);
    mask-size: cover;
    width: 8px;
    height: 14px;
    position: absolute;
    right: 4px;
    top: 7px;
  }
`;

const Select = styled.select`
  outline: none;
  font-size: 15px;
  padding: 5px 20px 5px 5px;
  display: block;
  width: 100%;
  border: none;
  border-bottom: 1px solid ${getColorFromCode('exa black', '#757575')};
  background-color: #fff;
  box-sizing: border-box;
  height: 33px;
  margin-top: 3px;
  appearance: none;

  & :focus ~ label {
    top: -20px;
    font-size: 14px;
    color: ${getColorFromCode('exa blue', 'var(--accent-color)')};
  }
`;

const TextInput = styled.input<{ error?: boolean; readonly?: boolean }>`
  outline: none;
  font-size: 16px;
  padding: 5px 20px 5px 5px;
  display: block;
  width: 100%;
  border: none;
  border-bottom: 1px solid ${props => (props.error ? getColorFromCode('exa pink', '#757575') : getColorFromCode('exa black', '#757575'))};
  background-color: #fff;
  height: 33px;
  box-sizing: border-box;
  margin-top: 3px;
  & :disabled {
    cursor: not-allowed;
    background-color: #d2d2d2;
  }
  ${props =>
    props.readonly
      ? css`
          cursor: not-allowed;
          background-color: #d2d2d2;
        `
      : ''}
`;

const CustomStyledButton: any = styled(CustomizableButton)`
  font-size: calc(16px + 0.5vw);
  margin: 20px;
`;

const ButtonPadder = styled.span`
  padding: 0 10px;
  & span {
    display: block;
    font-size: 18px;
    font-weight: bold;
  }
`;

const ContinueShoppingLink = styled.a`
  color: var(--accent-color);
  margin: 0 auto;
  font-size: calc(8px + 0.5vw);
  text-decoration: underline;
  &:hover {
    color: var(--main-text);
    text-decoration: none;
  }
`;

const Note = styled.div`
  font-size: 11px;
  text-align: center;
  margin: 40px;
`;

const SubmittedIndicator = styled.div`
  height: 130px;
  width: 130px;
  display: flex;
  mask-image: url(${checkMark});
  mask-size: cover;
  background-color: var(--accent-color);
  margin: 0 auto;
`;

const SubmittedIndicatorContainer = styled.div`
  display: flex;
  flex-direction: column;

  margin-left: auto;
  margin-right: auto;
  justify-content: center;

  padding-top: 30px;
`;

const SuccessText = styled.p`
  padding: 50px;
  font-size: calc(18px + 0.55vw);
  text-align: center;
`;

const SuccessDetailsText = styled.p`
  text-align: center;
  margin-bottom: 20px;
`;

const HeaderResellerBaseline = styled.div`
  color: var(--accent-color);
  margin-top: 15px;
  line-height: calc(10px + 0.7vw);
  font-size: calc(8px + 0.5vw);
`;
